import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { CheckCircle2, CreditCard, Banknote, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useBooking } from "./BookingProvider";
import { CONTACT } from "../../data/flyzone";

const emptyForm = {
  full_name: "",
  email: "",
  phone: "",
  pickup_date: "",
  return_date: "",
  pickup_time: "10:00",
  plan: "daily",
  payment_method: "pay_on_pickup",
  notes: "",
};

export const BookingDialog = () => {
  const { open, setOpen, selectedVehicle, vehicles, API } = useBooking();
  const [form, setForm] = useState(emptyForm);
  const [vehicleId, setVehicleId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [waUrl, setWaUrl] = useState("");

  useEffect(() => {
    if (open) {
      setForm(emptyForm);
      setDone(false);
      setVehicleId(selectedVehicle?.id || "");
    }
  }, [open, selectedVehicle]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const currentVehicle = vehicles.find((v) => v.id === vehicleId);

  const estimate = useMemo(() => {
    if (!currentVehicle || !form.pickup_date || !form.return_date) return null;
    const diff = (new Date(form.return_date) - new Date(form.pickup_date)) / 86400000;
    if (diff < 0) return null;
    const days = Math.max(1, Math.round(diff) || 1);
    let units, rate, unitLabel;
    if (form.plan === "weekly") {
      units = Math.ceil(days / 7); rate = currentVehicle.weekly; unitLabel = units > 1 ? "weeks" : "week";
    } else if (form.plan === "monthly") {
      units = Math.ceil(days / 30); rate = currentVehicle.monthly; unitLabel = units > 1 ? "months" : "month";
    } else {
      units = days; rate = currentVehicle.daily; unitLabel = units > 1 ? "days" : "day";
    }
    return { days, units, rate, unitLabel, total: units * rate };
  }, [currentVehicle, form.pickup_date, form.return_date, form.plan]);

  const submit = async (e) => {
    e.preventDefault();
    if (!vehicleId) {
      toast.error("Please select a vehicle.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        vehicle_id: vehicleId,
        vehicle_name: currentVehicle?.name || "Vehicle",
        ...form,
      });
      const payLabel = form.payment_method === "online" ? "Pay Online" : "Pay at Pickup";
      const lines = [
        "*New Booking Enquiry — Flyzone*",
        "",
        `*Vehicle:* ${currentVehicle?.name || "-"}`,
        `*Plan:* ${form.plan.charAt(0).toUpperCase() + form.plan.slice(1)}`,
        `*Pickup:* ${form.pickup_date} at ${form.pickup_time}`,
        `*Return:* ${form.return_date}`,
        estimate ? `*Duration:* ${estimate.days} day(s)` : null,
        estimate ? `*Est. Total:* ₹${estimate.total.toLocaleString("en-IN")}` : null,
        "",
        `*Name:* ${form.full_name}`,
        `*Phone:* ${form.phone}`,
        `*Email:* ${form.email}`,
        `*Payment:* ${payLabel}`,
        form.notes ? `*Notes:* ${form.notes}` : null,
      ].filter(Boolean);
      const url = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(lines.join("\n"))}`;
      setWaUrl(url);
      setDone(true);
      toast.success("Booking request confirmed! Sending details on WhatsApp…");
      window.open(url, "_blank");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="booking-dialog"
        className="bg-[#111111] border-white/10 text-white max-w-lg max-h-[92vh] overflow-y-auto"
      >
        {done ? (
          <div className="py-8 text-center" data-testid="booking-success">
            <CheckCircle2 className="w-16 h-16 text-primary mx-auto" />
            <h3 className="mt-5 text-2xl font-display font-bold">Booking Confirmed!</h3>
            <p className="mt-3 text-white/70">
              Thank you, {form.full_name || "rider"}. Your request for{" "}
              <span className="text-primary font-medium">{currentVehicle?.name}</span> is in.
              Tap below to send your booking details to us on WhatsApp for instant confirmation.
            </p>
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="booking-whatsapp-btn"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white hover:bg-[#20bd5a] font-semibold px-8 py-3 transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              Send details on WhatsApp
            </a>
            <div>
              <Button
                data-testid="booking-close-btn"
                onClick={() => setOpen(false)}
                variant="ghost"
                className="mt-3 text-white/60 hover:text-white"
              >
                Close
              </Button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-display">Book Your Ride</DialogTitle>
              <DialogDescription className="text-white/60">
                Fill in your details and we'll reserve your vehicle instantly.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label className="text-white/80">Vehicle</Label>
                <Select value={vehicleId} onValueChange={setVehicleId}>
                  <SelectTrigger data-testid="booking-vehicle" className="bg-[#0A0A0A] border-white/15">
                    <SelectValue placeholder="Select a vehicle" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/15 text-white">
                    {vehicles.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name} — ₹{v.daily.toLocaleString("en-IN")}/day
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2 col-span-2 sm:col-span-1">
                  <Label className="text-white/80">Full Name</Label>
                  <Input data-testid="booking-name" required value={form.full_name} onChange={set("full_name")} placeholder="John Rider" className="bg-[#0A0A0A] border-white/15" />
                </div>
                <div className="space-y-2 col-span-2 sm:col-span-1">
                  <Label className="text-white/80">Phone</Label>
                  <Input data-testid="booking-phone" required value={form.phone} onChange={set("phone")} placeholder="+1 555 000 1234" className="bg-[#0A0A0A] border-white/15" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white/80">Email</Label>
                <Input data-testid="booking-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" className="bg-[#0A0A0A] border-white/15" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-white/80">Pickup Date</Label>
                  <Input data-testid="booking-pickup-date" type="date" required value={form.pickup_date} onChange={set("pickup_date")} className="bg-[#0A0A0A] border-white/15" />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/80">Return Date</Label>
                  <Input data-testid="booking-return-date" type="date" required value={form.return_date} onChange={set("return_date")} className="bg-[#0A0A0A] border-white/15" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-white/80">Pickup Time</Label>
                  <Input data-testid="booking-time" type="time" required value={form.pickup_time} onChange={set("pickup_time")} className="bg-[#0A0A0A] border-white/15" />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/80">Rental Plan</Label>
                  <Select value={form.plan} onValueChange={(v) => setForm((f) => ({ ...f, plan: v }))}>
                    <SelectTrigger data-testid="booking-plan" className="bg-[#0A0A0A] border-white/15">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141414] border-white/15 text-white">
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white/80">Payment Method</Label>
                <RadioGroup
                  value={form.payment_method}
                  onValueChange={(v) => setForm((f) => ({ ...f, payment_method: v }))}
                  className="grid grid-cols-2 gap-3"
                >
                  <Label
                    htmlFor="pay_pickup"
                    className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer transition-colors ${form.payment_method === "pay_on_pickup" ? "border-primary bg-primary/10" : "border-white/15"}`}
                  >
                    <RadioGroupItem value="pay_on_pickup" id="pay_pickup" data-testid="pay-pickup" />
                    <Banknote className="w-4 h-4 text-primary" />
                    <span className="text-sm">Pay at Pickup</span>
                  </Label>
                  <Label
                    htmlFor="pay_online"
                    className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer transition-colors ${form.payment_method === "online" ? "border-primary bg-primary/10" : "border-white/15"}`}
                  >
                    <RadioGroupItem value="online" id="pay_online" data-testid="pay-online" />
                    <CreditCard className="w-4 h-4 text-primary" />
                    <span className="text-sm">Pay Online</span>
                  </Label>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label className="text-white/80">Notes (optional)</Label>
                <Textarea data-testid="booking-notes" value={form.notes} onChange={set("notes")} placeholder="Any special requests?" className="bg-[#0A0A0A] border-white/15 resize-none" rows={2} />
              </div>

              {estimate && (
                <div
                  data-testid="booking-estimate"
                  className="rounded-xl border border-primary/30 bg-primary/10 p-4 space-y-1.5"
                >
                  <div className="flex justify-between text-sm text-white/70">
                    <span>Duration</span>
                    <span className="text-white">{estimate.days} {estimate.days > 1 ? "days" : "day"}</span>
                  </div>
                  <div className="flex justify-between text-sm text-white/70">
                    <span>Rate</span>
                    <span className="text-white">
                      ₹{estimate.rate.toLocaleString("en-IN")} × {estimate.units} {estimate.unitLabel}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-white/10">
                    <span className="text-sm font-medium">Estimated Total</span>
                    <span data-testid="estimate-total" className="text-xl font-bold gold-text">
                      ₹{estimate.total.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <p className="text-[11px] text-white/40 pt-1">
                    Indicative estimate. Deposit &amp; final amount confirmed at pickup.
                  </p>
                </div>
              )}

              <Button
                type="submit"
                data-testid="booking-submit"
                disabled={submitting}
                className="w-full rounded-full bg-primary text-black hover:bg-primary/90 font-semibold py-6"
              >
                {submitting ? "Confirming…" : "Confirm Booking"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
